import {
  CirculatingWalletLiveViewResponse,
  CollectorWalletConceptDetailResponse,
  CollectorWalletDetailResponse,
  CollectorWalletLiveViewResponse,
  GeneralWalletLiveViewResponse,
  LegacyCollectorWalletDetailResponse,
  WalletLiveViewCatalogItem,
  WalletRechargesLiveViewResponse,
} from "../../../domain/models/accounting/wallet-live-view.model";

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

const isNullableFiniteNumber = (value: unknown): value is number | null =>
  value === null || isFiniteNumber(value);

const isString = (value: unknown): value is string => typeof value === "string";

const isCatalogItem = (value: unknown): value is WalletLiveViewCatalogItem =>
  isRecord(value) &&
  isFiniteNumber(value["id"]) &&
  isString(value["code"]) &&
  isString(value["name"]);

const isNullableString = (value: unknown): value is string | null =>
  value === null || isString(value);

const isNullableCatalogItem = (value: unknown): boolean =>
  value === null ||
  isRecord(value) &&
  isNullableFiniteNumber(value["id"]) &&
  isNullableString(value["code"]) &&
  isNullableString(value["name"]);

const isNullableRechargeDetail = (value: unknown): boolean =>
  value === null ||
  isRecord(value) &&
  isNullableCatalogItem(value["financialInstitution"]) &&
  isNullableCatalogItem(value["currency"]) &&
  isNullableFiniteNumber(value["amount"]);

const isCirculatingDistribution = (value: unknown): boolean =>
  isRecord(value) &&
  isNullableFiniteNumber(value["amount"]) &&
  isNullableFiniteNumber(value["percentage"]) &&
  isNullableFiniteNumber(value["variationPercentage"]);

export function extractWalletLiveViewPayload(value: unknown): unknown {
  return isRecord(value) && "payload" in value ? value["payload"] : value;
}

const hasCommonFields = (value: UnknownRecord): boolean =>
  isNullableFiniteNumber(value["idCompany"]) &&
  isNullableFiniteNumber(value["idAccountingDay"]) &&
  isNullableFiniteNumber(value["idCurrency"]);

const isLegacyCollectorDetail = (
  value: unknown,
): value is LegacyCollectorWalletDetailResponse =>
  isRecord(value) &&
  isFiniteNumber(value["idFinancialInstitution"]) &&
  isFiniteNumber(value["idCurrency"]) &&
  isFiniteNumber(value["amount"]);

const isCollectorDetail = (value: unknown): value is CollectorWalletDetailResponse =>
  isRecord(value) &&
  isCatalogItem(value["financialInstitution"]) &&
  isCatalogItem(value["currency"]) &&
  isFiniteNumber(value["amount"]);

const isCollectorConceptDetail = (
  value: unknown,
): value is CollectorWalletConceptDetailResponse =>
  isRecord(value) &&
  isCatalogItem(value["concept"]) &&
  isFiniteNumber(value["amount"]) &&
  isFiniteNumber(value["percentage"]);

export function isCollectorWalletLiveViewResponse(
  value: unknown,
): value is CollectorWalletLiveViewResponse {
  return isRecord(value) &&
    isNullableFiniteNumber(value["idCompany"]) &&
    isNullableFiniteNumber(value["idAccountingDay"]) &&
    (isNullableFiniteNumber(value["idCurrency"]) ||
      value["idCurrency"] === undefined &&
      (value["currency"] === null || isCatalogItem(value["currency"]))) &&
    isFiniteNumber(value["totalAmount"]) &&
    Array.isArray(value["details"]) &&
    value["details"].every((detail) =>
      isCollectorConceptDetail(detail) ||
      isCollectorDetail(detail) ||
      isLegacyCollectorDetail(detail));
}

export function isWalletRechargesLiveViewResponse(
  value: unknown,
): value is WalletRechargesLiveViewResponse {
  return isRecord(value) &&
    isNullableFiniteNumber(value["idCompany"]) &&
    isNullableFiniteNumber(value["idAccountingDay"]) &&
    isNullableCatalogItem(value["currency"]) &&
    isNullableFiniteNumber(value["totalAmount"]) &&
    (value["details"] === null ||
      Array.isArray(value["details"]) && value["details"].every(isNullableRechargeDetail));
}

export function isCirculatingWalletLiveViewResponse(
  value: unknown,
): value is CirculatingWalletLiveViewResponse {
  return isRecord(value) &&
    hasCommonFields(value) &&
    isFiniteNumber(value["totalAmount"]) &&
    isNullableFiniteNumber(value["variationPercentage"]) &&
    (value["previousPeriod"] === null || isString(value["previousPeriod"])) &&
    isCirculatingDistribution(value["companyWallets"]) &&
    isCirculatingDistribution(value["memberWallets"]) &&
    isCirculatingDistribution(value["availableWallets"]);
}

export function isGeneralWalletLiveViewResponse(
  value: unknown,
): value is GeneralWalletLiveViewResponse {
  return isRecord(value) &&
    hasCommonFields(value) &&
    isFiniteNumber(value["availableAmount"]) &&
    isFiniteNumber(value["provisionAmount"]) &&
    isFiniteNumber(value["accountingAmount"]) &&
    isNullableFiniteNumber(value["sourceTotal"]) &&
    isNullableFiniteNumber(value["calculatedTotal"]) &&
    isNullableFiniteNumber(value["differenceAmount"]);
}
